export type TourScheduleFieldKey =
  | "propertyName"
  | "streetAddress"
  | "addressLine2"
  | "city"
  | "state"
  | "zipCode";

export type TourScheduleFieldVisibility = Record<TourScheduleFieldKey, boolean>;

export const DEFAULT_TOUR_SCHEDULE_FIELD_VISIBILITY: TourScheduleFieldVisibility =
  {
    propertyName: true,
    streetAddress: true,
    addressLine2: true,
    city: true,
    state: true,
    zipCode: true,
  };

export const TOUR_SCHEDULE_FIELD_OPTIONS: Array<{
  key: TourScheduleFieldKey;
  label: string;
}> = [
  { key: "propertyName", label: "Property Name" },
  { key: "streetAddress", label: "Street Address" },
  { key: "addressLine2", label: "Address Line 2" },
  { key: "city", label: "City" },
  { key: "state", label: "State" },
  { key: "zipCode", label: "Zip code" },
];

const TOUR_SCHEDULE_FIELD_VISIBILITY_KEY = "tourScheduleFieldVisibility";
const TOUR_SCHEDULE_SHOW_DIRECTIONS_KEY = "tourScheduleShowDirections";

export const DEFAULT_TOUR_SCHEDULE_SHOW_DIRECTIONS = true;

const parseSurveyConfig = (surveyConfig?: string): Record<string, unknown> => {
  if (!surveyConfig) {
    return {};
  }

  try {
    const parsed = JSON.parse(surveyConfig);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

export const getTourScheduleFieldVisibility = (
  surveyConfig?: string,
): TourScheduleFieldVisibility => {
  const parsed = parseSurveyConfig(surveyConfig);
  const rawValue = parsed[TOUR_SCHEDULE_FIELD_VISIBILITY_KEY];

  if (!rawValue || typeof rawValue !== "object") {
    return DEFAULT_TOUR_SCHEDULE_FIELD_VISIBILITY;
  }

  const nextVisibility = { ...DEFAULT_TOUR_SCHEDULE_FIELD_VISIBILITY };

  for (const key of Object.keys(
    DEFAULT_TOUR_SCHEDULE_FIELD_VISIBILITY,
  ) as TourScheduleFieldKey[]) {
    const value = (rawValue as Partial<TourScheduleFieldVisibility>)[key];
    if (typeof value === "boolean") {
      nextVisibility[key] = value;
    }
  }

  return nextVisibility;
};

export const updateTourScheduleFieldVisibility = (
  surveyConfig: string | undefined,
  visibility: TourScheduleFieldVisibility,
): string => {
  const parsed = parseSurveyConfig(surveyConfig);

  return JSON.stringify({
    ...parsed,
    [TOUR_SCHEDULE_FIELD_VISIBILITY_KEY]: visibility,
  });
};

export const getTourScheduleShowDirections = (
  surveyConfig?: string,
): boolean => {
  const parsed = parseSurveyConfig(surveyConfig);
  const rawValue = parsed[TOUR_SCHEDULE_SHOW_DIRECTIONS_KEY];

  return typeof rawValue === "boolean"
    ? rawValue
    : DEFAULT_TOUR_SCHEDULE_SHOW_DIRECTIONS;
};

export const updateTourScheduleShowDirections = (
  surveyConfig: string | undefined,
  showDirections: boolean,
): string => {
  const parsed = parseSurveyConfig(surveyConfig);

  return JSON.stringify({
    ...parsed,
    [TOUR_SCHEDULE_SHOW_DIRECTIONS_KEY]: showDirections,
  });
};